import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './css/index.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import useInputState from '../../helpers/hooks/useInputState';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import SearchInput from '../../components/SearchInput';
import SelectButton from '../../components/SelectButton';
import SelectCity from './components/SelectCity';
import SelectEventCategory from './components/SelectEventCategory';
import ContactsSection from './components/ContactsSection';
import SelectPhotos from './components/SelectPhotos';
import checkAddEventPayload from '../../helpers/checkAddEventPayload';
import MyModal from '../../components/MyModal';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import EmptyHeight from '../../components/EmptyHeight';
import isValidDate from '../../helpers/isValidDate';
import MapView from '../AddOrganization/components/MapView';
import { getCountriesRequest } from '../../store/actions/staticData';
import { fetchOrganizationAutofillCitiesData } from '../../helpers/organizationAutofill';
import {
  buildEventAutofillCommonFileContent,
  buildEventAutofillCitiesFileContent,
  fetchEventAutofillCommonData,
  fetchEventAutofillDataForSelectedCountry,
  parseEventAutofillJson,
} from '../../helpers/eventAutofill';
import {
  buildAllCitiesSelection,
  isAllCitiesSelection,
} from '../../helpers/allCitiesOfCountry';
import { parseHoldingDatesListForForm } from '../../helpers/formatHoldingDatesList';
import HoldingDatesSection from './components/HoldingDatesSection';

const tempComponentStates = {
  city: 'city',
  photos: 'photos',
  eventCategory: 'eventCategory',
};

function AddEvent() {
  const dispatch = useDispatch();
  const { countries: countriesForAutofill } = useSelector((store) => store.staticData);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('id');
  const isEditing = Boolean(eventId);

  const [gettingLoading, setGettingLoading] = useState(isEditing);
  const [gettingError, setGettingError] = useState('');

  const [ownerId, setOwnerId] = useInputState();
  const [name, setName] = useInputState();
  const [holdingDates, setHoldingDates] = useState(['']);
  const [holdingTime, setHoldingTime] = useInputState();
  const [legacyHoldingDate, setLegacyHoldingDate] = useState('');
  const [dateStart, setDateStart] = useInputState();
  const [dateEnd, setDateEnd] = useInputState();
  const [city, setCity] = useState(null);
  const [address, setAddress] = useInputState();
  const [coordinates, setCoordinates] = useState(null);
  const [contacts, setContacts] = useState({});
  const [description, setDescription] = useInputState();
  const [photos, setPhotos] = useState([]);
  const [eventCategory, setEventCategory] = useState(null);

  const [tempComponentState, setTempComponentState] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalState, setModalState] = useState('');
  const [loading, setLoading] = useState(false);

  const [dropdownAddresses, setDropdownAddresses] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  const [isAutofillModalOpen, setIsAutofillModalOpen] = useState(false);
  const [autofillJsonText, setAutofillJsonText] = useState('');
  const [autofillError, setAutofillError] = useState('');
  const [autofillDownloadCommonLoading, setAutofillDownloadCommonLoading] = useState(false);
  const [autofillDownloadCitiesLoading, setAutofillDownloadCitiesLoading] = useState(false);
  const [autofillConvertLoading, setAutofillConvertLoading] = useState(false);
  const [autofillCountryId, setAutofillCountryId] = useState('');

  const autofillCountriesSorted = useMemo(() => {
    const list = countriesForAutofill || [];
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
  }, [countriesForAutofill]);

  const isInAllCitiesOfCountry = useMemo(() => isAllCitiesSelection(city), [city]);

  const handleCityChange = (nextCity) => {
    setCity(nextCity);
    if (isAllCitiesSelection(nextCity)) {
      setAddress('');
      setCoordinates(null);
    }
  };

  const openAutofillModal = () => {
    setAutofillError('');
    setAutofillCountryId((prev) => prev || city?.country_id || '');
    setIsAutofillModalOpen(true);
  };

  const closeAutofillModal = () => {
    setIsAutofillModalOpen(false);
    setAutofillError('');
  };

  const downloadTextFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onDownloadAutofillCommonFileClick = () => {
    setAutofillError('');
    setAutofillDownloadCommonLoading(true);
    fetchEventAutofillCommonData()
      .then((payload) => {
        downloadTextFile(buildEventAutofillCommonFileContent(payload), 'nomad-event-autofill-common.txt');
      })
      .catch(() => setAutofillError('Не удалось загрузить справочники. Проверьте сеть и повторите.'))
      .finally(() => setAutofillDownloadCommonLoading(false));
  };

  const onDownloadAutofillCitiesFileClick = () => {
    setAutofillError('');
    if (!autofillCountryId) {
      setAutofillError('Выберите страну — второй файл только с городами этой страны.');
      return;
    }
    setAutofillDownloadCitiesLoading(true);
    fetchOrganizationAutofillCitiesData(autofillCountryId)
      .then(({ selectedCountry, cities }) => {
        const content = buildEventAutofillCitiesFileContent({ selectedCountry, cities });
        const safe = String(selectedCountry.name || 'cities').replace(/[^\w\-]+/g, '_').slice(0, 48);
        downloadTextFile(content, `nomad-event-autofill-cities-${safe}.txt`);
      })
      .catch(() => setAutofillError('Не удалось загрузить города. Проверьте сеть и повторите.'))
      .finally(() => setAutofillDownloadCitiesLoading(false));
  };

  const onAutofillConvert = async () => {
    setAutofillError('');
    if (!autofillCountryId) {
      setAutofillError('Выберите ту же страну, для которой был скачан справочник (города).');
      return;
    }
    setAutofillConvertLoading(true);
    try {
      const staticData = await fetchEventAutofillDataForSelectedCountry(autofillCountryId);
      const result = parseEventAutofillJson(autofillJsonText, staticData);
      if (result.error) {
        setAutofillError(result.error);
        return;
      }
      const { patches } = result;

      if (patches.ownerId !== undefined) setOwnerId(patches.ownerId);
      if (patches.name !== undefined) setName(patches.name);
      if (patches.holdingDates !== undefined) setHoldingDates(patches.holdingDates);
      if (patches.holdingTime !== undefined) setHoldingTime(patches.holdingTime, true);
      if (patches.dateStart !== undefined) setDateStart(patches.dateStart);
      if (patches.dateEnd !== undefined) setDateEnd(patches.dateEnd);
      if (patches.address !== undefined) setAddress(patches.address);
      if (patches.description !== undefined) setDescription(patches.description);

      if (patches.eventCategory !== undefined) setEventCategory(patches.eventCategory);
      if (patches.city !== undefined) handleCityChange(patches.city);

      if (patches.coordinates !== undefined) {
        setTimeout(() => setCoordinates(patches.coordinates), 300);
      }

      if (patches.contactsPartial !== undefined) {
        setContacts((prev) => ({ ...prev, ...patches.contactsPartial }));
      }

      setIsAutofillModalOpen(false);
      setAutofillJsonText('');
    } catch (e) {
      setAutofillError('Ошибка при загрузке справочников или применении данных.');
    } finally {
      setAutofillConvertLoading(false);
    }
  };

  const onCancelClick = () => {
    navigate('/events/view');
  };

  const onSave = async () => {
    const { errors, formData, payload } = checkAddEventPayload({
      owner_id: ownerId,
      name,
      country_id: city?.country_id,
      city_id: isInAllCitiesOfCountry ? undefined : city?._id,
      holding_dates: holdingDates,
      holding_time: holdingTime,
      date_start: dateStart,
      date_end: dateEnd,
      address,
      coordinates,
      contacts,
      description,
      photos,
      withPhotos: !isEditing,
      events_category_id: eventCategory?._id,
      is_in_all_cities_of_country: isInAllCitiesOfCountry,
      allow_missing_holding_dates: Boolean(isEditing && legacyHoldingDate),
    });

    if (errors?.length) {
      setModalText(`В следующих полях есть ошибки:\n\n${errors.join('\n\n')}`);
      setIsModalVisible(true);
      setModalState('error');
      return;
    }

    if (!isEditing) {
      const data = await sendRequest({
        request: Api.createEvent,
        payload: formData,
        warnErrorText: 'while creating event',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Мероприятие "${name}" успешно было создано!`);
        setModalState('success');
      }
    } else {
      const data = await sendRequest({
        request: Api.editEvent,
        payload: { payload, id: eventId },
        warnErrorText: 'while editing event',
        setLoading,
      });

      setIsModalVisible(true);
      if (data?.status !== 'ok') {
        const errorText = data?.message || 'Не удалось установить соединение с сервером.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      } else {
        setModalText(`Мероприятие "${name}" успешно было редактировано!`);
        setModalState('success');
      }
    }
  };

  const onCloseModal = () => {
    if (modalState === 'error') {
      setIsModalVisible(false);
      setTimeout(() => {
        setModalText('');
        setModalState('');
      }, 400);
    } else if (modalState === 'success') {
      setIsModalVisible(false);
      navigate('/events/view');
    }
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getEvent,
      payload: { id: eventId },
      warnErrorText: 'while getting single event',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    const selectedEvent = data.event;
    setOwnerId(selectedEvent.user._id);
    setName(selectedEvent.name);

    const parsedDates = parseHoldingDatesListForForm(selectedEvent.holding_dates_list);
    setHoldingDates(parsedDates.dates);
    setHoldingTime(parsedDates.time, true);
    const hasStructuredDates = Array.isArray(selectedEvent.holding_dates_list)
      && selectedEvent.holding_dates_list.length > 0;
    setLegacyHoldingDate(
      !hasStructuredDates && selectedEvent.holding_date
        ? String(selectedEvent.holding_date)
        : '',
    );

    setDateStart(isValidDate(new Date(selectedEvent.date_start)) ? moment(selectedEvent.date_start).format('YYYY-MM-DD') : '');
    setDateEnd(isValidDate(new Date(selectedEvent.date_end)) ? moment(selectedEvent.date_end).format('YYYY-MM-DD') : '');
    setAddress(selectedEvent.address);
    setDescription(selectedEvent.description || '');
    setEventCategory(selectedEvent.events_category);

    delete selectedEvent.contacts.__v;
    delete selectedEvent.contacts._id;
    setContacts(selectedEvent.contacts);

    if (selectedEvent.is_in_all_cities_of_country) {
      setCity(buildAllCitiesSelection(selectedEvent.country));
      setCoordinates(null);
    } else {
      setCity(selectedEvent.city
        ? { ...selectedEvent.city, is_in_all_cities_of_country: false }
        : null);
      setTimeout(() => {
        setCoordinates(selectedEvent.coordinates);
      }, 300);
    }
  };

  const onAddressPress = async (item) => {
    setAddress(item.address_main);
    const data = await sendRequest({
      request: Api.getAddressCoordinates,
      payload: { params: { place_id: item.place_id } },
    });

    if (data?.coordinates) {
      setCoordinates(data.coordinates);
    }
  };

  const onMapPress = async (cords) => {
    const data = await sendRequest({
      request: Api.getPlaceNameWithCoordinates,
      payload: { params: cords },
    });

    if (data?.place_name) {
      setAddress(data.place_name);
    }
  };

  const getDropdownAddresses = async ({ signal, addressVal, cityId }) => {
    const data = await sendRequest({
      request: Api.getDropdownAddresses,
      payload: { params: { address: addressVal, city_id: cityId }, signal },
      shouldLogError: false,
      setLoading: setDropdownLoading,
    });

    if (data?.status !== 'ok') {
      return setDropdownAddresses([]);
    }

    setDropdownAddresses(data.addresses);
  };

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    if (!address?.length || !city?._id) {
      setDropdownAddresses([]);
    } else {
      getDropdownAddresses({
        signal,
        addressVal: address,
        cityId: city._id,
      }).then();
    }

    return () => controller.abort({ response: { data: 'Aborted' } });
  }, [address, city]);

  useEffect(() => {
    dispatch(getCountriesRequest());
  }, []);

  useEffect(() => {
    if (isEditing) {
      initData().then();
    }
  }, [isEditing]);

  return (
    <div className="add_event">
      <PageTitle
        text={isEditing ? 'Редактировать мероприятие' : 'Добавить мероприятие'}
        RightComponent={(
          <div className="add_event_button_group">
            <MyButton
              text="Отменить"
              onClick={onCancelClick}
              className="add_event_button_group_cancel"
              secondary
            />
            {!gettingError && (
            <MyButton
              text="Сохранить"
              onClick={onSave}
              className="add_event_button_group_save"
              loading={loading}
            />
            )}
          </div>
        )}
      />
      {!isEditing && !gettingError && (
        <MyButton
          text="Автозаполнение"
          onClick={openAutofillModal}
          className="add_event_autofill_fab"
        />
      )}
      {gettingLoading
        ? <p className="add_event_info_text">Загрузка</p>
        : gettingError
          ? <p className="add_event_info_text">{gettingError}</p>
          : (
            <div className="add_event_container">
              <SearchInput
                wrapperClassName="add_event_container_form_input"
                placeholder="ID владельца"
                withLeftIcon={false}
                value={ownerId}
                onChange={setOwnerId}
                shouldValueConsiderAsFocus={false}
              />

              <SearchInput
                wrapperClassName="add_event_container_form_input"
                placeholder="Название"
                withLeftIcon={false}
                value={name}
                onChange={setName}
                shouldValueConsiderAsFocus={false}
              />

              <HoldingDatesSection
                dates={holdingDates}
                time={holdingTime}
                onDatesChange={setHoldingDates}
                onTimeChange={setHoldingTime}
                legacyHoldingDate={legacyHoldingDate}
                dateStart={dateStart}
                dateEnd={dateEnd}
                onDateStartChange={setDateStart}
                onDateEndChange={setDateEnd}
              />

              <EmptyHeight height={16} />
              <SelectButton
                label="Категория события"
                value={eventCategory?.name}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.eventCategory}
                isActive={tempComponentState === tempComponentStates.eventCategory}
              />
              <SelectButton
                label="Город"
                value={city?.name}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.city}
                isActive={tempComponentState === tempComponentStates.city}
              />
              {isInAllCitiesOfCountry ? (
                <SearchInput
                  wrapperClassName="add_event_container_form_input"
                  placeholder="Адрес"
                  withLeftIcon={false}
                  withCloseIcon={false}
                  value={city?.country?.name || ''}
                  onChange={() => {}}
                  shouldValueConsiderAsFocus={false}
                  disabled
                />
              ) : city?._id ? (
                <SearchInput
                  wrapperClassName="add_event_container_form_input"
                  placeholder="Адрес"
                  withLeftIcon={false}
                  value={address}
                  onChange={setAddress}
                  shouldValueConsiderAsFocus={false}
                  withDropdown
                  dropdownItems={dropdownAddresses}
                  dropdownLabelField="address_full"
                  dropdownKeyField="place_id"
                  onDropdownItemPress={onAddressPress}
                  dropdownLoading={dropdownLoading}
                />
              ) : null}
              {!isInAllCitiesOfCountry && (
                <MapView
                  city={city}
                  coordinates={coordinates}
                  setCoordinates={setCoordinates}
                  onMapPress={onMapPress}
                />
              )}
              <ContactsSection
                contacts={contacts}
                setContacts={setContacts}
              />
              <SearchInput
                wrapperClassName="add_event_container_descr"
                withLeftIcon={false}
                placeholder="Описание"
                value={description}
                onChange={setDescription}
                isLarge
                shouldValueConsiderAsFocus={false}
              />
              {!isEditing && (
              <SelectButton
                label="Добавить фото"
                value={photos.length ? `${photos.length} фото` : ''}
                setTempComponentState={setTempComponentState}
                componentStateToSet={tempComponentStates.photos}
                isActive={tempComponentState === tempComponentStates.photos}
              />
              )}
            </div>
          )}

      {tempComponentState && (
      <div className="add_event_temp_container">
        {tempComponentState === tempComponentStates.eventCategory ? (
          <SelectEventCategory
            setValue={setEventCategory}
            setTempComponentState={setTempComponentState}
          />
        ) : tempComponentState === tempComponentStates.city ? (
          <SelectCity
            setValue={handleCityChange}
            setTempComponentState={setTempComponentState}
          />
        ) : tempComponentState === tempComponentStates.photos ? (
          <SelectPhotos
            setValue={setPhotos}
            setTempComponentState={setTempComponentState}
            initValue={photos}
          />
        ) : null}
      </div>
      )}

      <MyModal isOpen={isModalVisible} closeFunction={onCloseModal}>
        <div className="add_event_modal">
          <pre>{modalText}</pre>

          <MyButton
            className="add_event_modal_button"
            text="Ок"
            onClick={onCloseModal}
          />
        </div>
      </MyModal>

      <MyModal isOpen={isAutofillModalOpen} closeFunction={closeAutofillModal} style={{ width: 'min(560px, 92vw)' }}>
        <div className="add_event_autofill_modal">
          <p className="add_event_autofill_modal_title">Автозаполнение из JSON</p>
          <p className="add_event_autofill_modal_text">
            Два файла: общий без городов (только категории мероприятий) — один для всех стран.
            Второй — города страны (скачивается для каждой страны отдельно).
            Отправляйте оба в один чат модели одним сообщением подряд, затем по инструкции присылайте текст о мероприятии; ответ вставляйте сюда (допускается ```json … ```).
          </p>
          <p className="add_event_autofill_modal_hint">
            Если ответ ИИ не принимается системой из‑за неверной структуры или синтаксиса JSON, откройте
            {' '}
            <a
              href="https://jsonviewer.stack.hu/"
              target="_blank"
              rel="noopener noreferrer"
              className="add_event_autofill_modal_link"
            >
              jsonviewer.stack.hu
            </a>
            , вставьте текст и нажмите «Format» — так проще увидеть ошибку и выровнять структуру; при необходимости исправьте по подсказкам и скопируйте JSON обратно в поле ниже.
          </p>
          <div className="add_event_autofill_modal_country">
            <label className="add_event_autofill_modal_country_label" htmlFor="autofill-event-country">
              Страна для второго файла (только города)
            </label>
            <select
              id="autofill-event-country"
              className="add_event_autofill_modal_country_select"
              value={autofillCountryId}
              onChange={(e) => setAutofillCountryId(e.target.value)}
            >
              <option value="">— Выберите страну —</option>
              {autofillCountriesSorted.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <p className="add_event_autofill_modal_country_note">
              Выберите страну перед вторым скачиванием. «Преобразовать» — с той же страной.
            </p>
          </div>
          <MyButton
            text="Скачать общий файл"
            onClick={onDownloadAutofillCommonFileClick}
            className="add_event_autofill_modal_download"
            loading={autofillDownloadCommonLoading}
          />
          <MyButton
            text="Скачать файл городов страны"
            onClick={onDownloadAutofillCitiesFileClick}
            className="add_event_autofill_modal_download_secondary"
            loading={autofillDownloadCitiesLoading}
            disabled={!autofillCountryId}
          />
          <textarea
            className="add_event_autofill_modal_textarea"
            placeholder="Вставьте JSON сюда…"
            value={autofillJsonText}
            onChange={(e) => setAutofillJsonText(e.target.value)}
            rows={14}
            spellCheck={false}
          />
          {autofillError ? (
            <p className="add_event_autofill_modal_error">{autofillError}</p>
          ) : null}
          <div className="add_event_autofill_modal_actions">
            <MyButton
              text="Отмена"
              onClick={closeAutofillModal}
              secondary
              className="add_event_autofill_modal_cancel"
            />
            <MyButton
              text="Преобразовать"
              onClick={onAutofillConvert}
              className="add_event_autofill_modal_submit"
              loading={autofillConvertLoading}
              disabled={!autofillCountryId}
            />
          </div>
        </div>
      </MyModal>
    </div>

  );
}

export default AddEvent;
